import React from "react";
import { useSelector } from "react-redux";
import useMaintenanceStatus from "./useMaintenanceStatus";
import RBAC from "../../General/roleModuleActions";

// Roles that are NOT subject to maintenance mode — they keep working (and are never logged
// out) while a window is active. Deliberately Developer-ONLY: Administrator is subject to
// maintenance like every other role. Callers may override via `bypassRoles`, but the default
// must stay Developer-only so a maintenance window is a true lockout for everyone else.
const DEFAULT_BYPASS_ROLES = [RBAC.Role_Gentell_Developer];

/**
 * Wraps the app and swaps in a maintenance screen whenever a window is active —
 * EXCEPT for users holding a bypass role (admins by default), who pass straight
 * through and are never logged out.
 *
 * Rendering and the callback are intentionally kept minimal/stable — the live
 * detection all lives in {@link useMaintenanceStatus}; this component only
 * decides who is affected and what they see.
 *
 * @param {Object} props
 * @param {(status: import("./useMaintenanceStatus").MaintenanceStatus) => void} [props.onMaintenanceStart]
 *        Fired once per distinct maintenance window (dedup on tokensInvalidatedAt),
 *        for AFFECTED users only. Use it to log the user out / clear tokens.
 * @param {string[]} [props.bypassRoles] Role names exempt from maintenance mode.
 *        Defaults to Administrator.
 * @param {React.ReactNode} props.children
 */
export default function MaintenanceGate({ onMaintenanceStart, bypassRoles, children }) {
    const roleList = useSelector((s) => s && s.auth && s.auth.user && s.auth.user.roles && s.auth.user.roles.roleList) || [];
    const exemptRoles = bypassRoles && bypassRoles.length ? bypassRoles : DEFAULT_BYPASS_ROLES;
    const canBypass = roleList.some((r) => r && exemptRoles.includes(r.roleName));

    // For a bypassing user, suppress the logout callback entirely (pass undefined) so the
    // hook tracks status but never acts, and never render the maintenance screen for them.
    const { isMaintenance } = useMaintenanceStatus(canBypass ? undefined : onMaintenanceStart);

    if (isMaintenance && !canBypass) {
        return <MaintenanceScreen />;
    }
    return children;
}

function MaintenanceScreen() {
    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "2rem",
                background: "#f5f6fa",
            }}
        >
            <div style={{ maxWidth: 480 }}>
                <i
                    className="fas fa-tools"
                    style={{ fontSize: 48, color: "#6c757d", marginBottom: 24 }}
                />
                <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 12 }}>
                    We&apos;ll be right back
                </h1>
                <p style={{ color: "#6c757d", marginBottom: 0 }}>
                    Fastcare is undergoing scheduled maintenance. You&apos;ve
                    been signed out to keep your data safe. Please check back in
                    a little while.
                </p>
            </div>
        </div>
    );
}
